import React, { useState, useEffect } from 'react';
import { apiList, apiCreate, apiUpdate, apiDelete, apiPost } from '../api/apiClient';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { UserForm } from './forms/UserForm';
import { refreshAllCounters } from '../utils/refreshCounters';
import { useAuth } from '../hooks/useAuth';

interface UsersListProps {
  /** Filter: 'integrator' filters by integrator_id, 'customer' by customer_id, 'property' by property_ids containing the id */
  filterType: 'integrator' | 'dealer' | 'customer' | 'property';
  filterId: number | string;
  title?: string;
  /** For filterType='property': dealer_id of the property — used to include
   *  dealer-role users that have visibility into this property. */
  propertyDealerId?: string | number | null;
  /** For filterType='property': customer_id of the property — used to include
   *  customer-role users that have visibility into this property. */
  propertyCustomerId?: string | number | null;
}

// Role tier — higher number = higher in the hierarchy. The Web Users tab
// only shows roles BELOW the viewer's tier (per RBAC spec: "I Should Not
// See any users above me").
const ROLE_TIER: Record<string, number> = {
  super_admin: 100,
  admin: 90,
  dealer: 60,
  integrator: 60,
  customer: 40,
  customer_admin: 40,
  property_manager: 20,
  property: 20,
  manager: 20,
  viewer: 10,
  tenant: 5,
};
const tierFor = (role?: string) => ROLE_TIER[(role ?? '').toLowerCase()] ?? 0;

export const canSeeManagedUser = (viewerRole?: string, targetRole?: string): boolean => {
  const normalizedTarget = (targetRole ?? '').toLowerCase();
  if (normalizedTarget === 'super_admin') return false;
  const viewerTier = tierFor(viewerRole);
  return viewerTier > 0 && tierFor(targetRole) <= viewerTier;
};

const roleBadgeColor: Record<string, string> = {
  admin: 'red',
  super_admin: 'red',
  integrator: 'blue',
  dealer_admin: 'blue',
  customer: 'green',
  customer_admin: 'green',
  property_manager: 'purple',
};

const roleLabel: Record<string, string> = {
  admin: 'Admin',
  super_admin: 'Super Admin',
  integrator: 'Dealer',
  dealer_admin: 'Dealer Admin',
  customer: 'Customer',
  customer_admin: 'Customer Admin',
  property_manager: 'Property Manager',
};

export const UsersList: React.FC<UsersListProps> = ({ filterType, filterId, title, propertyDealerId, propertyCustomerId }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const allUsers = await apiList('users');
      let filtered: any[];
      if (filterType === 'integrator' || filterType === 'dealer') {
        filtered = allUsers.filter((u: any) => u.integrator_id === filterId || u.dealer_id === filterId);
      } else if (filterType === 'customer') {
        filtered = allUsers.filter((u: any) => u.customer_id === filterId);
      } else {
        // property — show every web user with VISIBILITY into this property,
        // not just users whose property_id literally matches. That means:
        //   • property_managers explicitly tied to this property (single
        //     property_id field or property_ids[] / property_ids_json array)
        //   • customer-role users whose customer_id matches the property's
        //     customer (they see all properties under that customer)
        //   • dealer-role users whose dealer_id matches the property's
        //     dealer (they see every property under that dealer)
        const pid = String(filterId);
        const dId = propertyDealerId != null ? String(propertyDealerId) : null;
        const cId = propertyCustomerId != null ? String(propertyCustomerId) : null;
        filtered = allUsers.filter((u: any) => {
          const role = (u.role ?? '').toLowerCase();
          if (role === 'property_manager' || role === 'property' || role === 'manager') {
            if (u.property_id && String(u.property_id) === pid) return true;
            const pids = u.property_ids ?? u.property_ids_json;
            const arr = Array.isArray(pids)
              ? pids
              : (typeof pids === 'string' ? (() => { try { return JSON.parse(pids) || []; } catch { return []; } })() : []);
            return arr.map(String).includes(pid);
          }
          if (role === 'customer' || role === 'customer_admin') {
            return cId != null && String(u.customer_id) === cId;
          }
          if (role === 'dealer' || role === 'dealer_admin' || role === 'integrator' || role === 'integrator_admin') {
            return dId != null && (String(u.dealer_id) === dId || String(u.integrator_id) === dId);
          }
          return false;
        });
      }
      // The API has already constrained the organization/property scope.
      // Within that scope, peers must remain visible so customer owners and
      // property managers can manage one another; roles above the viewer and
      // all super-admin accounts remain hidden.
      filtered = filtered.filter((u: any) => canSeeManagedUser(currentUser?.role, u.role));
      setUsers(filtered);
    } catch (e) {
      console.error('Failed to load users:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filterId) load();
  }, [filterType, filterId, propertyDealerId, propertyCustomerId, currentUser?.role]); // eslint-disable-line

  const handleCreateUser = async (data: any) => {
    await apiCreate('users', data);
    refreshAllCounters();
    setCreateModalOpen(false);
    await load();
  };

  // Determine preset props based on filter type. Entity-detail "Add User"
  // defaults to the admin variant so the new user can administer this entity.
  // (Non-admin scoped users can still be created from the global Web Users page.)
  const getPresetProps = () => {
    if (filterType === 'integrator' || filterType === 'dealer') {
      return { presetIntegratorId: filterId, presetRole: 'dealer_admin' };
    } else if (filterType === 'customer') {
      return { presetCustomerId: filterId, presetRole: 'customer_admin' };
    } else if (filterType === 'property') {
      return { presetPropertyId: filterId, presetRole: 'property_manager' };
    }
    return {};
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-gray-700">{title || ''}</span>
        <Button size="sm" onClick={() => setCreateModalOpen(true)}>
          Add User
        </Button>
      </div>
      {loading ? (
        <div className="py-4 text-center text-gray-400 text-sm">Loading users...</div>
      ) : users.length === 0 ? (
        <div className="py-4 text-center text-gray-400 text-sm">No users found</div>
      ) : (
      <div className="divide-y divide-gray-100">
        {users.map((u: any) => (
          <button
            key={u.id}
            type="button"
            onClick={() => { setEditing(u); setActionMsg(null); }}
            className="w-full flex items-center justify-between py-3 px-1 hover:bg-gray-50 text-left"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                {(u.last_name?.[0] || '').toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.email || `User #${u.id}`}
                </p>
                <p className="text-xs text-gray-500">{u.email || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {u.phone && <span className="text-xs text-gray-400">{u.phone}</span>}
              <Badge color={roleBadgeColor[u.role] || 'gray'}>{roleLabel[u.role] || u.role}</Badge>
              <span className={`w-2 h-2 rounded-full ${u.status === 'active' ? 'bg-green-400' : 'bg-gray-300'}`} title={u.status || 'unknown'} />
            </div>
          </button>
        ))}
      </div>
      )}

      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} title="Add User">
        <UserForm
          onSubmit={handleCreateUser}
          onCancel={() => setCreateModalOpen(false)}
          {...getPresetProps()}
        />
      </Modal>

      <Modal open={!!editing} onClose={() => { setEditing(null); setActionMsg(null); }} title={editing ? `Edit ${editing.first_name || editing.email || 'user'}` : 'Edit User'}>
        {editing && (
          <div className="space-y-4">
            <UserForm
              initialData={editing}
              onSubmit={async (data) => {
                setBusyAction('save');
                try {
                  await apiUpdate('users', editing.id, data);
                  refreshAllCounters();
                  setActionMsg('Saved.');
                  await load();
                  setEditing(null);
                } catch (e: any) {
                  setActionMsg('Save failed: ' + (e?.message ?? e));
                } finally {
                  setBusyAction(null);
                }
              }}
              onCancel={() => setEditing(null)}
            />
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <p className="text-xs font-semibold text-gray-700">Account actions</p>
              <div className="flex flex-wrap gap-2">
                {editing.status === 'locked' && (
                  <Button
                    size="sm"
                    disabled={!!busyAction}
                    onClick={async () => {
                      setBusyAction('unlock');
                      setActionMsg(null);
                      try {
                        await apiPost(`/auth/users/${editing.id}/unlock`);
                        setActionMsg('Account unlocked. Failed login attempts were cleared.');
                        setEditing({ ...editing, status: 'active', login_attempts: 0, locked_until: null });
                        await load();
                      } catch (e: any) {
                        setActionMsg('Unlock failed: ' + (e?.message ?? e));
                      } finally {
                        setBusyAction(null);
                      }
                    }}
                  >
                    {busyAction === 'unlock' ? 'Unlocking…' : 'Unlock account'}
                  </Button>
                )}
                <Button
                  size="sm"
                  disabled={!!busyAction}
                  onClick={async () => {
                    if (!window.confirm(`Reset password for ${editing.email}? A new random password will be generated and shown once.`)) return;
                    setBusyAction('reset');
                    setActionMsg(null);
                    try {
                      const apiBase = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
                      const tk = localStorage.getItem('intrlock_token') || '';
                      // Prefer the bulk reset endpoint scoped to a single user — doesn't exist as one-shot,
                      // so just generate and POST /auth/set-password
                      const alpha = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
                      const pw = Array.from({ length: 12 }, () => alpha[Math.floor(Math.random() * alpha.length)]).join('');
                      const res = await fetch(`${apiBase}/auth/set-password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                        body: JSON.stringify({ user_id: editing.id, password: pw }),
                      });
                      if (!res.ok) throw new Error(await res.text());
                      setActionMsg(`New password: ${pw}  (copy it now — it won't be shown again)`);
                    } catch (e: any) {
                      setActionMsg('Reset failed: ' + (e?.message ?? e));
                    } finally {
                      setBusyAction(null);
                    }
                  }}
                >
                  {busyAction === 'reset' ? 'Resetting…' : 'Reset password'}
                </Button>
                <Button
                  size="sm"
                  disabled={!!busyAction}
                  onClick={async () => {
                    const next = editing.status === 'active' ? 'inactive' : 'active';
                    setBusyAction('status');
                    try {
                      await apiUpdate('users', editing.id, { status: next });
                      setActionMsg(`Status set to ${next}.`);
                      await load();
                      setEditing({ ...editing, status: next });
                    } catch (e: any) {
                      setActionMsg('Status change failed: ' + (e?.message ?? e));
                    } finally {
                      setBusyAction(null);
                    }
                  }}
                >
                  {editing.status === 'active' ? 'Deactivate' : 'Reactivate'}
                </Button>
                <Button
                  size="sm"
                  disabled={!!busyAction}
                  onClick={async () => {
                    if (!window.confirm(`Delete user ${editing.email}? This cannot be undone.`)) return;
                    setBusyAction('delete');
                    try {
                      await apiDelete('users', editing.id);
                      setActionMsg('User deleted.');
                      await load();
                      setEditing(null);
                    } catch (e: any) {
                      setActionMsg('Delete failed: ' + (e?.message ?? e));
                    } finally {
                      setBusyAction(null);
                    }
                  }}
                >
                  {busyAction === 'delete' ? 'Deleting…' : 'Delete user'}
                </Button>
              </div>
              {actionMsg && (
                <p className="text-xs mt-2 text-gray-700 break-all">{actionMsg}</p>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
